import React, { useState, useCallback, useEffect } from "react";
import { useParams, useLocation, useSearchParams } from "wouter";
import * as Tabs from "@radix-ui/react-tabs";
import { sortBy } from "lodash";

import { useQueryState } from "../../../../../components/src/use-query-state";
import { DateHelper, emptyObjectId, SwedenCalendar } from "../../../../../common/src";
import { FinancialStatementsTab } from "./tabs/FinancialStatementsTab";
import { usePrevious } from "../../../../../components/src/common/Utils";
import {
    CurrencyEnum,
    Instrument,
    PermissionAssetEnum,
    TransactionStatus,
    PositionSelectorType,
    TradingManagerColumn
} from "../../../types.generated";
import { ReconciliationTab } from "./tabs/Reconciliation";
import { ISelectDropdownOption, SelectDropdown } from "../../../components/SelectDropdown";
import { SYSTEM_PARTY_ID } from "../../../Constants";
import { useQuery } from "urql";
import {
    GET_ACCOUNTING_RUNS,
    GET_ACCOUNTING_RUNS_WITH_JOURNAL_ENTRIES,
    GET_CLIENTS,
    GET_POSITIONS,
    GET_T_ACCOUNT_CHARTS
} from "../../../../../graphql/src/accounting/queries";
import { AccountingRun, Me, Party, TAccountChart } from "../../../types.generated";
import { CustomAutosuggest } from "../../../../../components/src/form";
import { ResponsiveTabsList } from "../../../../../components/src/ResponsiveTabsList";

import { ClientOption } from "./tabs/ClientOption";

const allTabs = {
    financialstatements: "Financial statements",
    reconciliation: "Reconciliation"
};

const defaultClient = {
    value: emptyObjectId,
    label: "None",
    _id: emptyObjectId,
    instruments: [],
    accountingCurrency: CurrencyEnum.SEK,
    accountingCompanyType: null,
    types: []
};

export function StatementsReportingPage(): React.ReactElement {
    const [, navigate] = useLocation();
    const [queryArgs] = useSearchParams();
    const { tab } = useParams<{ tab: string }>();

    const today = new Date().toISOString().slice(0, 10);
    const [date, setDate] = useQueryState("date", today);
    const [clientId, setClientId] = useQueryState("clientId", emptyObjectId);
    const [tabs, setTabs] = useState<Record<string, string>>(allTabs);
    const [selectedClient, setSelectedClient] = useState(defaultClient);
    const [dates, setDates] = useState([
        {
            key: today,
            text: today
        }
    ]);
    const [extendDates, setExtendDates] = useState(false);

    const [selectedAccountingRun, setSelectedAccountingRun] = useState(null);
    const [yearAccountingRuns, setYearAccountingRuns] = useState([]);
    const [latestTAccountChart, setLatestTAccountChart] = useState(null);
    const [previousAccountingRuns, setPreviousAccountingRuns] = useState([]);

    const dateOptionsLimit = 20;

    const [{ fetching: loadingClients, error: errorClients, data: dataClients }] = useQuery<{
        clients?: Partial<Party>[];
        me?: Partial<Me>;
    }>({
        query: GET_CLIENTS,
        requestPolicy: "network-only"
    });
    const [{ data: dataTAccountCharts, fetching: loadingTAccountCharts, error: errorTAccountCharts }] = useQuery<{
        tAccountCharts?: Partial<TAccountChart>[];
    }>({
        query: GET_T_ACCOUNT_CHARTS,
        variables: { clientId },
        pause: !clientId || clientId === emptyObjectId
    });

    const [{ fetching: loadingAccountingRuns, error: errorAccountingRuns, data: getAccountingRunsData }] = useQuery<{
        accountingRuns?: Partial<AccountingRun>[];
        previousAccountingRuns?: Partial<AccountingRun>[];
    }>({
        query: GET_ACCOUNTING_RUNS_WITH_JOURNAL_ENTRIES,
        pause: !clientId || clientId === emptyObjectId,
        variables: {
            clientId,
            accountingPeriod: date.substring(0, 4),
            previousAccountingPeriod: (parseFloat(date.substring(0, 4)) - 1).toString()
        },
        requestPolicy: "network-only"
    });

    const [{ fetching: loadingAllAccountingRuns, error: errorAllAccountingRuns, data: getAllAccountingRunsData }] = useQuery<{
        accountingRuns?: Partial<AccountingRun>[];
    }>({
        query: GET_ACCOUNTING_RUNS,
        pause: !clientId || clientId === emptyObjectId,
        variables: { clientId },
        requestPolicy: "network-only"
    });

    //Positions at end of previous year
    const [{ fetching: loadingPreviousPositions, error: errorPreviousPositions, data: dataPreviousPositions }] = useQuery<{
        tradingmanager?: Partial<TradingManagerColumn>[];
    }>({
        query: GET_POSITIONS,
        variables: {
            filter: {
                endDate: previousAccountingRuns.length > 0 ? previousAccountingRuns[previousAccountingRuns.length - 1].endDate : null,
                clientIds: [clientId],
                accountIds: null,
                statusIn: [
                    TransactionStatus.Confirmed,
                    TransactionStatus.Instructed,
                    TransactionStatus.Pending,
                    TransactionStatus.Preliminary,
                    TransactionStatus.Settled
                ]
            },
            groupPositionsBy: PositionSelectorType.ExternalAccountId
        },
        pause: !clientId || clientId === emptyObjectId
    });

    const [{ fetching: loadingPositions, error: errorPositions, data: dataPositions }] = useQuery<{
        tradingmanager?: Partial<TradingManagerColumn>[];
    }>({
        query: GET_POSITIONS,
        variables: {
            filter: {
                endDate: date,
                clientIds: [clientId],
                accountIds: null,
                statusIn: [
                    TransactionStatus.Confirmed,
                    TransactionStatus.Instructed,
                    TransactionStatus.Pending,
                    TransactionStatus.Preliminary,
                    TransactionStatus.Settled
                ]
            },
            groupPositionsBy: PositionSelectorType.ExternalAccountId
        },
        pause: !clientId || clientId === emptyObjectId || !date
    });

    const previousAccountingRunsData = usePrevious(getAccountingRunsData);

    let previousQuarter = new Date(new Date(date).getFullYear(), new Date(date).getMonth() - 2, 1);
    // Adjusting to prev business date if needed
    const swedenCalendar = new SwedenCalendar();
    if (!swedenCalendar.isBusinessDay(previousQuarter)) {
        while (!swedenCalendar.isBusinessDay(previousQuarter)) {
            previousQuarter = DateHelper.dateAddDays(previousQuarter, -1);
        }
    }

    useEffect(() => {
        if (dataClients) {
            const availableClientIds = dataClients.clients.map((client) => client._id);
            if (clientId !== emptyObjectId) {
                if (!availableClientIds.includes(clientId)) {
                    setClientId(null);
                } else if (!selectedClient || selectedClient._id !== clientId) {
                    for (let i = 0; i < dataClients.clients.length; i++) {
                        const client = dataClients.clients[i];
                        if (clientId === client._id && clientId !== emptyObjectId) {
                            setSelectedClient({
                                value: client._id,
                                label: client.name,
                                _id: client._id,
                                instruments: client.instruments,
                                accountingCurrency: client.accountingCurrency as CurrencyEnum,
                                accountingCompanyType: client.accountingCompanyType,
                                types: client.types
                            });

                            break;
                        }
                    }
                }

                if (
                    selectedClient &&
                    selectedClient._id !== emptyObjectId &&
                    getAccountingRunsData &&
                    (getAccountingRunsData.accountingRuns.length || getAccountingRunsData.previousAccountingRuns.length) &&
                    getAllAccountingRunsData &&
                    !loadingAccountingRuns &&
                    !loadingAllAccountingRuns &&
                    (getAccountingRunsData.accountingRuns.length || getAccountingRunsData.previousAccountingRuns.length) &&
                    (!selectedAccountingRun ||
                        (selectedAccountingRun &&
                            (selectedAccountingRun.client._id !== selectedClient._id || selectedAccountingRun.endDate !== date)))
                ) {
                    const availableDatesByDate: Record<string, { key: string; text: string }> = {};
                    const previousRuns: Array<Partial<AccountingRun>> = [];
                    const currentRuns: Array<Partial<AccountingRun>> = [];
                    const accountingRunsByDate: Record<string, Partial<AccountingRun>> = {};

                    const accountingRunsWithJournalEntries =
                        getAccountingRunsData.previousAccountingRuns && getAccountingRunsData.previousAccountingRuns.length
                            ? getAccountingRunsData.accountingRuns.concat(getAccountingRunsData.previousAccountingRuns)
                            : getAccountingRunsData.accountingRuns;

                    for (const accountingRun of getAllAccountingRunsData.accountingRuns) {
                        if (accountingRun.endDate) {
                            availableDatesByDate[accountingRun.endDate] = { key: accountingRun.endDate, text: accountingRun.endDate };
                        }
                    }

                    for (const accountingRun of accountingRunsWithJournalEntries) {
                        if (accountingRun.endDate) {
                            accountingRunsByDate[accountingRun.endDate] = accountingRun;

                            if (
                                accountingRun.endDate &&
                                accountingRun.endDate.substring(0, 4) === parseFloat(date.substring(0, 4)).toString() &&
                                accountingRun.endDate <= date
                            ) {
                                currentRuns.push(accountingRun);
                            }
                            if (
                                accountingRun.endDate &&
                                accountingRun.endDate.substring(0, 4) === (parseFloat(date.substring(0, 4)) - 1).toString()
                            ) {
                                previousRuns.push(accountingRun);
                            }
                        }
                    }

                    const availableDatesSorted = Object.values(availableDatesByDate).sort(function (a, b) {
                        return new Date(b.key).getTime() - new Date(a.key).getTime();
                    });

                    setDates(Object.values(availableDatesSorted));

                    if (!date || !availableDatesByDate[date]) {
                        const endDate =
                            queryArgs && queryArgs.get("date") && availableDatesByDate[queryArgs.get("date")]
                                ? queryArgs.get("date")
                                : availableDatesSorted[0].key;

                        setDate(endDate);
                    }

                    //When reloading page after switching to extended mode with search field for dates
                    if (
                        !extendDates &&
                        availableDatesSorted.length > dateOptionsLimit &&
                        !availableDatesSorted.slice(0, dateOptionsLimit).some((dateOption) => dateOption.key === date)
                    ) {
                        setDate(availableDatesSorted[0].key);
                    }

                    if (accountingRunsByDate[date]) {
                        setSelectedAccountingRun(accountingRunsByDate[date]);
                    }
                    setPreviousAccountingRuns(previousRuns);
                    setYearAccountingRuns(currentRuns);
                }
            }
        }
        if (
            selectedClient &&
            dataTAccountCharts &&
            dataTAccountCharts.tAccountCharts &&
            (!latestTAccountChart || latestTAccountChart.client._id !== selectedClient._id)
        ) {
            const sortedTAccountCharts = sortBy(dataTAccountCharts.tAccountCharts, "version");
            setLatestTAccountChart(sortedTAccountCharts[sortedTAccountCharts.length - 1]);
        }
    }, [
        clientId,
        dataClients,
        dataTAccountCharts,
        date,
        extendDates,
        getAccountingRunsData,
        getAllAccountingRunsData,
        latestTAccountChart,
        previousAccountingRunsData,
        queryArgs,
        selectedAccountingRun,
        selectedClient,
        loadingAccountingRuns,
        loadingAllAccountingRuns,
        setClientId,
        setDate
    ]);

    useEffect(() => {
        //console.log(`tab = ${tab}`);
        if (!tab || !(tab in allTabs)) {
            //console.log("Banana1");
            const uri = "/financialstatements/financialstatements" + (queryArgs.toString() ? `?${queryArgs.toString()}` : "");
            //console.log("uri", uri);
            navigate(uri, {
                replace: true
            });
        }
    }, [navigate, queryArgs, tab]);

    const onTabChange = useCallback(
        (tabId: string) => {
            if (!(tabId in tabs)) {
                navigate("/financialstatements/financialstatements" + (queryArgs.toString() ? `?${queryArgs.toString()}` : ""), {
                    replace: true
                });
            } else {
                if (tabId !== tab) {
                    navigate("/financialstatements/" + tabId + (queryArgs.toString() ? `?${queryArgs.toString()}` : ""), {
                        replace: true
                    });
                }
            }
        },
        [navigate, queryArgs, tab, tabs]
    );

    if (errorClients) return <p>errorClients: {JSON.stringify(errorClients, null, 2)}</p>;
    if (errorAccountingRuns) return <p>errorAccountingRuns: {JSON.stringify(errorAccountingRuns, null, 2)}</p>;
    if (errorAllAccountingRuns) return <p>errorAllAccountingRuns: {JSON.stringify(errorAllAccountingRuns, null, 2)}</p>;
    if (errorPositions)
        return (
            <div>
                <p>error:</p>
                <pre> {JSON.stringify(errorPositions, null, 2)}</pre>
            </div>
        );
    if (errorPreviousPositions)
        return (
            <div>
                <p>error:</p>
                <pre> {JSON.stringify(errorPreviousPositions, null, 2)}</pre>
            </div>
        );
    if (errorTAccountCharts) return <p>error tAccountCharts: {JSON.stringify(errorTAccountCharts, null, 2)}</p>;

    if (loadingClients) return <p>Loading clients</p>;
    if (loadingAccountingRuns || loadingAllAccountingRuns) return <p>Loading accounting runs</p>;
    if (loadingPositions || loadingPreviousPositions)
        return (
            <div>
                <p>Loading positions</p>
            </div>
        );
    if (loadingTAccountCharts) return <p>Loading tAccountCharts</p>;

    let options: ClientOption[] = [];

    //Check access client combined with asset accounting + transaction
    const accessClients: Array<Partial<Party>> = [];
    if (dataClients && dataClients.clients) {
        if (dataClients.me && dataClients.me.roles) {
            let access = false;
            for (const client of dataClients.clients) {
                if (dataClients.me.frontendRole.assets.length === 0) {
                    access = true;
                } else {
                    for (const role of dataClients.me.roles) {
                        if (
                            role.assets.includes(PermissionAssetEnum.Accounting) &&
                            role.assets.includes(PermissionAssetEnum.Transaction) &&
                            role.clientIds.includes(client._id)
                        ) {
                            access = true;
                        }
                    }
                }
                // Exclude system party from client selection menu
                if (access && client._id !== SYSTEM_PARTY_ID) {
                    accessClients.push(client);
                }
            }
        }
    }

    accessClients.forEach((client) => {
        if (client.instruments && client.instruments.length > 0) {
            options.push({
                value: client._id,
                label: client.name,
                _id: client._id,
                instruments: client.instruments as Instrument[], // Danger
                accountingCurrency: client.accountingCurrency as unknown as CurrencyEnum,
                accountingCompanyType: client.accountingCompanyType,
                types: client.types
            });
        }
    });
    options = sortBy(options, "label");

    const clients: ISelectDropdownOption[] = [
        ...[{ key: emptyObjectId, text: "None", data: defaultClient }],
        ...options.map((client) => {
            return { key: client._id, text: client.label, data: client };
        })
    ];

    const datesOptions: ISelectDropdownOption[] =
        dates.length > dateOptionsLimit ? [...dates.slice(0, dateOptionsLimit), ...[{ key: "...more", text: "...more" }]] : [...dates];

    return (
        <div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-4 bg-gray-100 p-4 rounded">
                    <h5>Client</h5>
                    <div className="flex flex-wrap -mx-2">
                        <div className="w-full sm:w-1/4 px-2 mb-4">
                            <SelectDropdown
                                placeholder="None"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                                defaultValue={selectedClient ? selectedClient._id : null}
                                options={clients}
                                onChange={(e: ISelectDropdownOption) => {
                                    setSelectedClient(e.data);
                                    setClientId(e.data._id);
                                    setTabs(allTabs);
                                }}
                            />
                        </div>
                    </div>
                    <br />
                    <h5>Date</h5>
                    <div className="flex flex-wrap -mx-2">
                        <div className="w-full sm:w-1/4 px-2 mb-4">
                            {!extendDates ? (
                                <SelectDropdown
                                    placeholder="None"
                                    className="width-auto w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                                    defaultValue={date}
                                    options={datesOptions}
                                    onChange={(e: ISelectDropdownOption) => {
                                        if (e) {
                                            if (e.text === "...more") {
                                                setExtendDates(true);
                                            } else {
                                                setDate(e.key);
                                            }
                                        }
                                    }}
                                />
                            ) : (
                                <CustomAutosuggest
                                    className={""}
                                    selected={{ _id: date, name: date }}
                                    values={dates.map((date) => {
                                        return { _id: date.key, name: date.key };
                                    })}
                                    toDisplayText={(d) => d.name}
                                    toSuggestionText={(d) => d.name}
                                    onChange={(option) => {
                                        setDate(option === null ? today : option._id);
                                    }}
                                />
                            )}
                        </div>
                    </div>
                    <br />
                    <Tabs.Root value={tab || "financialstatements"} onValueChange={onTabChange} className="w-full">
                        <ResponsiveTabsList value={tab || "financialstatements"} onValueChange={onTabChange}>
                            {"financialstatements" in tabs ? (
                                <Tabs.Trigger
                                    value="financialstatements"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    {tabs["financialstatements"]}
                                </Tabs.Trigger>
                            ) : null}
                            {"reconciliation" in tabs ? (
                                <Tabs.Trigger
                                    value="reconciliation"
                                    className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:border-gray-300 focus:outline-none focus:border-sky-500 data-[state=active]:border-sky-500 data-[state=active]:text-sky-600"
                                >
                                    {tabs["reconciliation"]}
                                </Tabs.Trigger>
                            ) : null}
                        </ResponsiveTabsList>
                        {"financialstatements" in tabs ? (
                            <Tabs.Content value="financialstatements" className="mt-4">
                                <FinancialStatementsTab
                                    client={selectedClient}
                                    date={date}
                                    selectedAccountingRun={selectedAccountingRun}
                                    accountingRuns={yearAccountingRuns}
                                    positions={
                                        dataPositions && dataPositions.tradingmanager
                                            ? (dataPositions.tradingmanager as TradingManagerColumn[])
                                            : []
                                    }
                                    previousPositions={
                                        dataPreviousPositions && dataPreviousPositions.tradingmanager && previousAccountingRuns.length
                                            ? (dataPreviousPositions.tradingmanager as TradingManagerColumn[])
                                            : []
                                    }
                                    latestTAccountChart={latestTAccountChart}
                                />
                            </Tabs.Content>
                        ) : null}
                        {"reconciliation" in tabs ? (
                            <Tabs.Content value="reconciliation" className="mt-4">
                                <ReconciliationTab
                                    selectedAccountingRun={selectedAccountingRun}
                                    accountingRuns={yearAccountingRuns}
                                    partyTypes={selectedClient ? selectedClient.types : []}
                                    positions={
                                        dataPositions && dataPositions.tradingmanager
                                            ? (dataPositions.tradingmanager as TradingManagerColumn[])
                                            : []
                                    }
                                    latestTAccountChart={latestTAccountChart}
                                    client={selectedClient}
                                />
                            </Tabs.Content>
                        ) : null}
                    </Tabs.Root>
                </div>
            </div>
        </div>
    );
}
